import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Brand, BrandManagementColumn, BrandSummary, SortState } from '../types';
import { RowClickedEvent, type ColDef, SortChangedEvent } from "ag-grid-community";
import { AgGridAngular } from 'ag-grid-angular';
import { AgTooltipCellComponent } from '../../../shared/components/ag-tooltip-cell/ag-tooltip-cell.component';
import { BrandNameLogoRendererComponent } from '../../../shared/components/brand-name-logo/brand-name-logo-renderer.component';
import { getManagementDateFormat } from '../../../utils/time';
import { MANAGEMENT_GRID_MODULES, MANAGEMENT_GRID_THEME, createManagementGridOptions, toSortState } from '../management-grid';

@Component({
  selector: 'app-brands-table',
  imports: [AgGridAngular],
  templateUrl: './brands-table.component.html',
  styleUrl: './brands-table.component.scss'
})
export class BrandsTableComponent {
  @Input() rows: Brand[] = [];
  @Output() rowClicked = new EventEmitter<Brand>();
  @Output() sortChanged = new EventEmitter<SortState>();

  readonly theme = MANAGEMENT_GRID_THEME;
  readonly modules = MANAGEMENT_GRID_MODULES;
  readonly gridOptions = createManagementGridOptions();
  colDefs: ColDef[] = this.getColDef();

  public onRowClicked = (event: RowClickedEvent): void => {
    this.rowClicked.emit(event.data as Brand);
  };

  public onSortChanged(event: SortChangedEvent): void {
    const sort = toSortState(event);
    if (sort) {
      this.sortChanged.emit(sort);
    }
  }

  private getColDef(): ColDef[] {
    return [
      {
        field: BrandManagementColumn.BRAND_KEY,
        cellRenderer: BrandNameLogoRendererComponent,
        flex: 2, // most important column — give it room so the key isn't cut off
        valueGetter: (params: any) => {
          return {
            name: params.data.brandKey,
            logoPath: params.data.logoPath
          };
        },
      },
      {
        field: BrandManagementColumn.BRAND_NAME,
      },
      {
        field: BrandManagementColumn.ACCOUNTS,
        headerName: "Accounts",
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          const domains = params.data?.[BrandManagementColumn.ACCOUNTS];
          return domains.join(', ');
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.ADVERTISER_DOMAINS,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          const domains: string[] = params.data?.[BrandManagementColumn.ADVERTISER_DOMAINS] ?? [];
          if (domains.length <= 1) {
            return domains[0] ?? '';
          }
          return `${domains[0]} +${domains.length - 1}`;
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.COMPETITORS,
        headerName: "Competitors",
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[BrandManagementColumn.COMPETITORS]
            .map((item: BrandSummary) => item?.name)
            .filter(Boolean)
            .join(', ');
        },
        sortable: false,
      },
      {
        field: BrandManagementColumn.CREATED_AT,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return getManagementDateFormat(params.data[BrandManagementColumn.CREATED_AT])
        }
      }
    ];
  }
}
